import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'ninja_reading_progress';
const MAX_ENTRIES = 50;

const getStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const persist = (data) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

export function useReadingProgress() {
  const [data, setData] = useState(getStored);

  const saveProgress = useCallback((slug, info) => {
    const stored = getStored();
    const existing = stored[slug] || {};
    stored[slug] = {
      ...existing,
      slug,
      title: info.title || existing.title,
      category: info.category || existing.category,
      featured_image: info.featured_image || existing.featured_image,
      read_time: info.read_time || existing.read_time,
      progress: Math.max(info.progress || 0, existing.progress || 0),
      lastRead: Date.now(),
    };
    // Prune old entries if over limit
    const entries = Object.entries(stored);
    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => b[1].lastRead - a[1].lastRead);
      const pruned = Object.fromEntries(entries.slice(0, MAX_ENTRIES));
      persist(pruned);
      setData(pruned);
    } else {
      persist(stored);
      setData(stored);
    }
  }, []);

  const getProgress = useCallback((slug) => {
    return getStored()[slug] || null;
  }, []);

  const getInProgress = useCallback(() => {
    const stored = getStored();
    return Object.values(stored)
      .filter(e => e.progress > 5 && e.progress < 95)
      .sort((a, b) => b.lastRead - a.lastRead);
  }, []);

  const getCompleted = useCallback(() => {
    const stored = getStored();
    return Object.values(stored)
      .filter(e => e.progress >= 95)
      .sort((a, b) => b.lastRead - a.lastRead);
  }, []);

  const clearProgress = useCallback((slug) => {
    const stored = getStored();
    delete stored[slug];
    persist(stored);
    setData(stored);
  }, []);

  const getRecentlyViewed = useCallback((limit = 6) => {
    const stored = getStored();
    return Object.values(stored)
      .sort((a, b) => b.lastRead - a.lastRead)
      .slice(0, limit);
  }, []);

  const clearAll = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setData({});
  }, []);

  return { data, saveProgress, getProgress, getInProgress, getCompleted, getRecentlyViewed, clearProgress, clearAll };
}
